import React, { useState } from 'react';

import { DateTime } from 'luxon';

import styled from '@emotion/styled';

import { Box, COLORS, Text, UnstyledButton } from '@clutter/clean';

import {
  AddressFragment,
  useGeocodeQuery,
  useOrderCancellationPolicyGroupQuery,
} from '@graphql/platform';

import { MovingCheckoutData } from '@root/components/checkout/data';
import {
  CheckoutType,
  MovingCheckoutStep,
  MovingCheckoutStepProps,
} from '@root/components/checkout/types';
import { WT_VISITOR_TOKEN } from '@root/initializers/wt';

import { Address } from '@shared/formatters/address';

import { ALL_DAY_DURATION } from '../product_pages/steps/shared/appointment/arrival_window_selector';
import { resolveAppointmentDateTime } from '../utilities/appointment_date';
import { BookingDetailsGrid } from './booking_details_grid';
import { BookingDetailsItem } from './booking_details_item';
import { EditContactModal } from './edit_contact_modal';

const LinkText = styled(Text.Callout)`
  text-decoration: underline;
`;

export const MovingBookingDetails = ({
  values,
  onEdit,
  onChange,
  onCancellationLinkClick,
}: {
  values: MovingCheckoutData;
  onEdit(step: MovingCheckoutStep): void;
  onChange: MovingCheckoutStepProps['onChange'];
  onCancellationLinkClick(): void;
}) => {
  const geocode =
    useGeocodeQuery({
      variables: { zip: values.zip! },
      skip: !values.zip,
    }).data?.geocode ?? undefined;
  const { data } = useOrderCancellationPolicyGroupQuery({
    variables: {
      visitorToken: WT_VISITOR_TOKEN,
      orderScheduledTime: values.dateScheduled?.fromTime,
    },
    skip: !geocode || geocode?.region?.name === 'Los Angeles',
  });

  const movingCancellationPolicy =
    data?.orderCancellationPolicyGroup?.movingCancellationPolicy;
  const cancelByDate = movingCancellationPolicy?.latestFreeCancellationDate
    ? DateTime.fromISO(movingCancellationPolicy?.latestFreeCancellationDate)
    : resolveAppointmentDateTime(values).minus({ days: 7 });

  const [modalOpen, setModalOpen] = useState(false);

  const formatDate = (date?: DateTime) =>
    date?.toLocaleString({
      day: 'numeric',
      weekday: 'short',
      month: 'long',
    });

  const formatAllDaySlot = (date: DateTime) => {
    const start = date
      .toLocal()
      .startOf('day')
      .set({ hour: 7, minute: 0, second: 0, millisecond: 0 });
    const end = start.plus({ hours: ALL_DAY_DURATION.hours });
    const timeFormat = {
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    } as const;
    return `${start.toLocaleString(timeFormat)} - ${end.toLocaleString(timeFormat)}`;
  };

  return (
    <>
      <Box margin="0 0 16px">
        <Text.Title size="extraSmall">Review booking details</Text.Title>
        {cancelByDate.toLocal().startOf('day') >
          DateTime.now().startOf('day') && (
          <Text.Callout color={COLORS.storm}>
            Cancel for free before {cancelByDate.toFormat('MMMM d')}.{' '}
            <UnstyledButton onClick={() => onCancellationLinkClick()}>
              <LinkText>Cancellation policy</LinkText>
            </UnstyledButton>
          </Text.Callout>
        )}
      </Box>
      <BookingDetailsGrid columns={2}>
        <BookingDetailsItem
          label="Contact"
          editable={values.checkoutType !== CheckoutType.Reonboarding}
          onEdit={() => setModalOpen(true)}
        >
          {values.name}
          <br />
          {values.email}
          <br />
          {values.phone}
        </BookingDetailsItem>
        <BookingDetailsItem
          label="Date & arrival time"
          onEdit={() => onEdit(MovingCheckoutStep.Appointment)}
        >
          {values.movingQuote?.longDistance ? (
            <>
              Pickup: {formatDate(values.datePreferred)},{' '}
              {values.dateScheduled?.formatted}
              <br />
              Delivery:{' '}
              {values.unpackingScheduled
                ? `${formatDate(values.unpackingScheduled)}, ${formatAllDaySlot(values.unpackingScheduled)}`
                : 'Schedule later'}
            </>
          ) : (
            <>
              {formatDate(values.datePreferred)}
              <br />
              {values.dateScheduled?.formatted}
            </>
          )}
        </BookingDetailsItem>
        <BookingDetailsItem
          label="Starting address"
          onEdit={() => onEdit(MovingCheckoutStep.Address)}
        >
          <Address address={(values.startAddress as AddressFragment) ?? {}} />
        </BookingDetailsItem>
        <BookingDetailsItem
          label="Destination address"
          onEdit={() => onEdit(MovingCheckoutStep.Address)}
        >
          <Address address={(values.endAddress as AddressFragment) ?? {}} />
        </BookingDetailsItem>
      </BookingDetailsGrid>
      <EditContactModal
        isOpen={modalOpen}
        onChange={onChange}
        values={{ name: values.name, email: values.email, phone: values.phone }}
        onClose={() => setModalOpen(false)}
      />
    </>
  );
};
